import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectors } from '@twaice-fe/frontend/shared/store';
import { ProductPackage, SystemDetails } from '@twaice-fe/shared/models';
import { distinctUntilChanged, map, Observable, of, switchMap } from 'rxjs';

const { configsSelectors, systemSelectors } = selectors;

/**
 * A page whose availability depends on the product package. Only pages that actually vary
 * between packages belong here — always-included ones are absent rather than listed.
 */
export enum PackageFeature {
  /** the performance sections other than Recoverable Energy */
  Performance = 'PERFORMANCE',
  RecoverableEnergy = 'RECOVERABLE_ENERGY',
  PenaltyRisk = 'PENALTY_RISK',
  Degradation = 'DEGRADATION',
  Anomalies = 'ANOMALIES',
  ErrorCodes = 'ERROR_CODES',
}

/**
 * Resolves feature availability from the product package of the selected system.
 *
 * Always reaches a verdict: a package it cannot resolve is treated as the full one, so
 * callers are never left waiting on an answer that may not come.
 */
@Injectable({ providedIn: 'root' })
export class ProductPackageService {
  private readonly store = inject(Store);

  /** The single source of truth for which pages each product package includes. */
  private readonly packageFeatures: Record<ProductPackage, ReadonlySet<PackageFeature>> = {
    [ProductPackage.Full]: new Set(Object.values(PackageFeature)),
    [ProductPackage.FullWithoutErrorCodes]: new Set(
      Object.values(PackageFeature).filter((feature) => feature !== PackageFeature.ErrorCodes)
    ),
    [ProductPackage.Basic]: new Set(),
    // Basic-legacy keeps Recoverable Energy while losing the other performance sections,
    // which is why the two are separate features
    [ProductPackage.BasicLegacy]: new Set([PackageFeature.RecoverableEnergy, PackageFeature.ErrorCodes]),
  };

  /**
   * The package of the selected system, since packages are sold per system.
   *
   * Anything the map cannot answer — no system selected yet, or a system ui-config sent no
   * package for — counts as the full package. Withholding a verdict instead would leave
   * pages blank and hold `canMatch` guards open, so an unknown package hides nothing.
   */
  private readonly productPackage$: Observable<ProductPackage> = this.store.select(systemSelectors.getSelected).pipe(
    switchMap((system: SystemDetails | undefined) =>
      system ? this.store.select(configsSelectors.getProductPackage(system.systemBk)) : of(null)
    ),
    map((productPackage) => productPackage ?? ProductPackage.Full),
    distinctUntilChanged()
  );

  hasFeature(feature: PackageFeature): Observable<boolean> {
    return this.productPackage$.pipe(
      map((productPackage) => this.packageIncludes(productPackage, feature)),
      distinctUntilChanged()
    );
  }

  hasFeatureForSystem(feature: PackageFeature, systemBk: string | null): Observable<boolean> {
    if (!systemBk) return this.hasFeature(feature);

    return this.store.select(configsSelectors.getProductPackage(systemBk)).pipe(
      map((productPackage) => this.packageIncludes(productPackage, feature)),
      distinctUntilChanged()
    );
  }

  packageIncludes(productPackage: ProductPackage | null | undefined, feature: PackageFeature): boolean {
    return this.packageFeatures[productPackage ?? ProductPackage.Full].has(feature);
  }
}
